/**
 * articleCard — card dell'articolo blog nella scheda corso (.ik-article-card).
 *
 * Chiusa: miniatura + titolo nella riga azioni. Il clic la apre a tutta colonna
 * (immagine grande + didascalia con il link all'articolo); un altro clic la richiude.
 * Il passaggio usa le View Transitions dove disponibili, altrimenti è istantaneo.
 *
 * Quando la card esce dallo schermo verso l'alto, la versione compatta compare in
 * sidebar (desktop) e la miniatura nella barra di acquisto (mobile): il clic riporta
 * alla card e la apre. Card, sidebar e barra stanno in punti diversi del DOM, quindi
 * lo stato vive nello store 'articleCard'.
 *
 * Tracking: emette 'ik:article-preview' all'apertura (hooks.js).
 *
 * Uso nel template:
 *   <div class="ik-article-card" x-data="articleCard"
 *        :class="{ 'ik-article-card--open': $store.articleCard.open }">
 *     <button class="ik-article-card__toggle" @click="$store.articleCard.toggle('card')">…</button>
 *   </div>
 *   <button class="ik-article-card ik-article-card--docked" x-show="$store.articleCard.docked"
 *           @click="$store.articleCard.reveal('sidebar')">…</button>
 */

// Fallback se 'scrollend' non arriva (browser senza supporto o nessuno scroll necessario)
const REVEAL_FALLBACK_MS = 800;

function reducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function registerArticleCard(Alpine) {
    Alpine.store('articleCard', {
        open: false,
        docked: false,
        el: null,

        setOpen(open, origin = 'card') {
            if (this.open === open) return;
            const apply = async () => {
                this.open = open;
                // La transizione fotografa il nuovo stato dopo il render di Alpine
                await Alpine.nextTick();
            };
            if (document.startViewTransition && !reducedMotion()) {
                document.startViewTransition(apply);
            } else {
                apply();
            }
            if (open) {
                window.dispatchEvent(new CustomEvent('ik:article-preview', { detail: { origin } }));
            }
        },

        toggle(origin = 'card') {
            this.setOpen(!this.open, origin);
        },

        /** Dalla sidebar o dalla barra: torna alla card e aprila. */
        reveal(origin) {
            if (!this.el) return;
            this.el.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'start' });
            let done = false;
            const openCard = () => {
                if (done) return;
                done = true;
                this.setOpen(true, origin);
            };
            if ('onscrollend' in window) {
                window.addEventListener('scrollend', openCard, { once: true });
            }
            setTimeout(openCard, REVEAL_FALLBACK_MS);
        },
    });

    Alpine.data('articleCard', () => ({
        _observer: null,

        init() {
            const store = Alpine.store('articleCard');
            store.el = this.$el;
            if (!('IntersectionObserver' in window)) return;
            this._observer = new IntersectionObserver(([entry]) => {
                // Solo quando esce verso l'alto: prima di arrivarci non serve il promemoria
                store.docked = !entry.isIntersecting && entry.boundingClientRect.top < 0;
            });
            this._observer.observe(this.$el);
        },

        destroy() {
            this._observer?.disconnect();
        },
    }));
}
